import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import {
  ASSESSMENT_DELETION_REASONS,
  AssessmentDeletionReason,
  formatProgramAssessmentRemovedBy,
  programAssessmentDeletionNoteForDisplay,
  programAssessmentDeletionReasonForDisplay,
  programAssessmentHasDeletionAuditMetadata,
  ProgramAssessmentAnswer,
  ProgramAssessmentDetailHeader,
  ProgramAssessmentListItem,
  deleteProgramAssessmentSafe,
  fetchProgramAssessmentDetailSafe
} from "./programAssessmentsAPI";
import { formatIsoDisplay } from "./programAssessmentsDisplay";

/** Same row as the table already showed; fills the drawer while detail API loads. */
function listRowToProvisionalHeader(row: ProgramAssessmentListItem): ProgramAssessmentDetailHeader {
  return {
    assessmentEventId: row.assessmentEventId,
    programId: row.programId,
    activityEventId: row.activityEventId,
    activityId: row.activity?.id ?? null,
    activityTitle: row.activity?.title ?? null,
    activityEventDate: row.activityEventDate,
    completionDate: row.completionDate,
    subjectUserId: row.subjectUserId,
    subjectFullName: row.subjectFullName,
    raterUserId: row.raterUserId,
    raterFullName: row.raterFullName,
    deletedAt: row.deletedAt,
    deletedReason: row.deletedReason,
    deletedNote: row.deletedNote ?? null,
    deletedByUserId: row.deletedByUserId,
    deletedByFullName: row.deletedByFullName
  };
}

type Props = {
  open: boolean;
  onClose: () => void;
  listRow: ProgramAssessmentListItem | null;
  onDeleted: (payload: {
    primaryAssessmentEventId: string;
    deletedAt: string;
    deletedReason: string;
    deletedNote: string | null;
  }) => void;
};

function SummaryBlock({
  title,
  header,
  t,
  showAttending = true
}: {
  title: string;
  header: ProgramAssessmentDetailHeader;
  t: (key: string, options?: Record<string, unknown>) => string;
  showAttending?: boolean;
}) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="subtitle1"
        component="h3"
        sx={{
          fontWeight: 700,
          color: "text.primary",
          lineHeight: 1.35,
          mb: 1
        }}
      >
        {title}
      </Typography>
      <Stack spacing={0.5}>
        <Typography variant="body2" color="text.secondary">
          <strong>{t("Activity")}:</strong> {header.activityTitle || header.activityId || "—"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>{t("Activity event date")}:</strong>{" "}
          {formatIsoDisplay(header.activityEventDate ?? undefined)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>{t("Completed")}:</strong> {formatIsoDisplay(header.completionDate ?? undefined)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>{t("Trainee")}:</strong>{" "}
          {header.subjectFullName || header.subjectUserId || "—"}
        </Typography>
        {showAttending ? (
          <Typography variant="body2" color="text.secondary">
            <strong>{t("Evaluator")}:</strong>{" "}
            {header.raterFullName || header.raterUserId || "—"}
          </Typography>
        ) : null}
        {programAssessmentHasDeletionAuditMetadata(header) ? (
          <>
            {header.deletedAt ? (
              <Typography variant="body2" color="warning.main">
                <strong>{t("Deleted")}:</strong> {formatIsoDisplay(header.deletedAt)}
              </Typography>
            ) : null}
            <Typography variant="body2" color="text.secondary">
              <strong>{t("Deletion reason")}:</strong>{" "}
              {programAssessmentDeletionReasonForDisplay(
                header.deletedReason,
                header.deletedNote
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
              <strong>{t("Deletion note")}:</strong>{" "}
              {programAssessmentDeletionNoteForDisplay(
                header.deletedReason,
                header.deletedNote
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>{t("Removed by")}:</strong>{" "}
              {formatProgramAssessmentRemovedBy(
                header.deletedByFullName,
                header.deletedByUserId,
                t
              )}
            </Typography>
          </>
        ) : null}
      </Stack>
    </Box>
  );
}

function AnswersList({
  answers,
  emptyLabel,
  t
}: {
  answers: ProgramAssessmentAnswer[];
  emptyLabel: string;
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  if (answers.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyLabel}
      </Typography>
    );
  }
  return (
    <Stack component="ul" spacing={1.25} sx={{ m: 0, pl: 2 }}>
      {answers.map((a) => (
        <Box component="li" key={a.answerEventId} sx={{ listStyle: "disc" }}>
          <Typography variant="body2" component="span" display="block">
            {a.questionTitle || a.questionId || t("Question")}
            {a.inputTypeName ? (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                ({a.inputTypeName})
              </Typography>
            ) : null}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
            {a.value?.trim() ? a.value : "—"}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

export function ProgramAssessmentDetailDrawer({
  open,
  onClose,
  listRow,
  onDeleted
}: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [primary, setPrimary] = useState<ProgramAssessmentDetailHeader | null>(null);
  const [primaryAnswers, setPrimaryAnswers] = useState<ProgramAssessmentAnswer[]>([]);
  const [selfAssessment, setSelfAssessment] = useState<ProgramAssessmentDetailHeader | null>(null);
  const [selfAnswers, setSelfAnswers] = useState<ProgramAssessmentAnswer[]>([]);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState<AssessmentDeletionReason | "">("");
  const [deleteNote, setDeleteNote] = useState("");
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState<string | null>(null);
  const deleteReasonIsOther = deleteReason === "Other";
  const trimmedDeleteNote = deleteNote.trim();
  const missingRequiredOtherNote = deleteReasonIsOther && !trimmedDeleteNote;

  /** Ignore late responses when the user switches rows or closes the drawer. */
  const detailFetchSeqRef = useRef(0);

  const resetDeleteUi = useCallback(() => {
    setDeleteOpen(false);
    setDeleteReason("");
    setDeleteNote("");
    setDeleteError(null);
    setDeleteSubmitting(false);
  }, []);

  const loadDetail = useCallback(
    async (options?: { preserveDeleteSuccess?: boolean }) => {
      if (!listRow?.assessmentEventId) {
        return;
      }
      const fetchSeq = ++detailFetchSeqRef.current;
      setLoading(true);
      setLoadError(null);
      setPrimary(null);
      setPrimaryAnswers([]);
      setSelfAssessment(null);
      setSelfAnswers([]);
      if (!options?.preserveDeleteSuccess) {
        setDeleteSuccess(null);
      }
      resetDeleteUi();
      const result = await fetchProgramAssessmentDetailSafe(listRow.assessmentEventId);
      if (fetchSeq !== detailFetchSeqRef.current) {
        return;
      }
      setLoading(false);
      if (!result.ok) {
        setPrimary(null);
        setPrimaryAnswers([]);
        setSelfAssessment(null);
        setSelfAnswers([]);
        setLoadError(result.message);
        return;
      }
      const { data } = result;
      setPrimary(data.primary);
      setPrimaryAnswers(data.primaryAnswers);
      setSelfAssessment(data.selfAssessment);
      setSelfAnswers(data.selfAnswers);
    },
    [listRow?.assessmentEventId, resetDeleteUi]
  );

  useEffect(() => {
    if (open && listRow?.assessmentEventId) {
      void loadDetail();
    }
    if (!open) {
      detailFetchSeqRef.current += 1;
      setLoading(false);
      setLoadError(null);
      setPrimary(null);
      setPrimaryAnswers([]);
      setSelfAssessment(null);
      setSelfAnswers([]);
      setDeleteSuccess(null);
      resetDeleteUi();
    }
  }, [open, listRow?.assessmentEventId, loadDetail, resetDeleteUi]);

  const handleClose = () => {
    onClose();
  };

  const handleConfirmDelete = async () => {
    if (!listRow?.assessmentEventId || !deleteReason) {
      return;
    }
    setDeleteSubmitting(true);
    setDeleteError(null);

    const result = await deleteProgramAssessmentSafe({
      assessmentEventId: listRow.assessmentEventId,
      reason: deleteReason as AssessmentDeletionReason,
      note: trimmedDeleteNote || null
    });
    setDeleteSubmitting(false);
    if (!result.ok) {
      setDeleteError(result.message);
      return;
    }
    const { data } = result;
    setDeleteSuccess(
      t("Assessment {{id}} was removed at {{time}}.", {
        id: data.assessmentEventId,
        time: formatIsoDisplay(data.deletedAt)
      }) as string
    );
    onDeleted({
      primaryAssessmentEventId: listRow.assessmentEventId,
      deletedAt: data.deletedAt,
      deletedReason: deleteReason as string,
      deletedNote: trimmedDeleteNote || null
    });
    void loadDetail({ preserveDeleteSuccess: true });
  };

  const primaryDeleted = Boolean(primary?.deletedAt);

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <Box
        sx={{
          width: { xs: "100vw", sm: 480 },
          maxWidth: "100vw",
          height: "100%",
          display: "flex",
          flexDirection: "column"
        }}
        role="presentation"
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: "divider"
          }}
        >
          <Typography variant="h6" component="h2">
            {t("Assessment Details")}
          </Typography>
          <IconButton aria-label={t("Close")} onClick={handleClose} edge="end" size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
          {!listRow ? (
            <Typography variant="body2" color="text.secondary">
              {t("Select an assessment from the table.")}
            </Typography>
          ) : loadError ? (
            <Alert severity="error">{loadError}</Alert>
          ) : loading || primary ? (
            <Stack spacing={2}>
              {deleteSuccess ? <Alert severity="success">{deleteSuccess}</Alert> : null}

              <SummaryBlock
                title={t("Evaluator Assessment")}
                header={primary ?? listRowToProvisionalHeader(listRow)}
                t={t}
              />

              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    py: 2
                  }}
                >
                  <CircularProgress size={22} />
                  <Typography variant="body2" color="text.secondary">
                    {t("Loading answers and linked self-assessment…")}
                  </Typography>
                </Box>
              ) : primary ? (
                <>
                  <Typography
                    variant="subtitle1"
                    component="h3"
                    sx={{ fontWeight: 700, color: "text.primary", lineHeight: 1.35 }}
                  >
                    {t("Answers")}
                  </Typography>
                  <AnswersList
                    answers={primaryAnswers}
                    emptyLabel={t("No answers recorded for this assessment.")}
                    t={t}
                  />

                  {selfAssessment ? (
                    <>
                      <Divider />
                      <SummaryBlock
                        title={t("Self Assessment")}
                        header={selfAssessment}
                        t={t}
                        showAttending={false}
                      />
                      <Typography
                        variant="subtitle1"
                        component="h3"
                        sx={{ fontWeight: 700, color: "text.primary", lineHeight: 1.35 }}
                      >
                        {t("Self Assessment Answers")}
                      </Typography>
                      <AnswersList
                        answers={selfAnswers}
                        emptyLabel={t("No answers recorded for the self-assessment.")}
                        t={t}
                      />
                    </>
                  ) : null}

                  <Divider />

                  {!primaryDeleted ? (
                    <Box>
                      {!deleteOpen ? (
                        <Button
                          type="button"
                          color="error"
                          variant="outlined"
                          onClick={() => {
                            setDeleteOpen(true);
                            setDeleteError(null);
                          }}
                        >
                          {t("Remove Assessment…")}
                        </Button>
                      ) : (
                        <Stack spacing={2} sx={{ pt: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            {t(
                              "This will delete this assessment and its answers, along with the linked self-assessment (if any). This cannot be undone."
                            )}
                          </Typography>
                          <FormControl size="small" required fullWidth>
                            <InputLabel id="pa-del-reason">{t("Deletion reason")}</InputLabel>
                            <Select
                              labelId="pa-del-reason"
                              label={t("Deletion reason")}
                              value={deleteReason}
                              onChange={(e) =>
                                setDeleteReason(e.target.value as AssessmentDeletionReason)
                              }
                            >
                              {ASSESSMENT_DELETION_REASONS.map((r) => (
                                <MenuItem key={r} value={r}>
                                  {r}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField
                            label={
                              deleteReasonIsOther ? t("Note (required for Other)") : t("Note (optional)")
                            }
                            value={deleteNote}
                            onChange={(e) => setDeleteNote(e.target.value)}
                            multiline
                            minRows={2}
                            size="small"
                            fullWidth
                            required={deleteReasonIsOther}
                            error={missingRequiredOtherNote}
                            helperText={
                              missingRequiredOtherNote
                                ? t("Please add a note when selecting Other.")
                                : " "
                            }
                          />
                          {deleteError ? <Alert severity="error">{deleteError}</Alert> : null}
                          <Stack direction="row" spacing={1}>
                            <Button
                              type="button"
                              variant="contained"
                              color="error"
                              disabled={!deleteReason || deleteSubmitting || missingRequiredOtherNote}
                              onClick={() => void handleConfirmDelete()}
                            >
                              {deleteSubmitting ? (
                                <CircularProgress size={22} color="inherit" />
                              ) : (
                                t("Confirm Removal")
                              )}
                            </Button>
                            <Button
                              type="button"
                              onClick={() => resetDeleteUi()}
                              disabled={deleteSubmitting}
                            >
                              {t("Cancel")}
                            </Button>
                          </Stack>
                        </Stack>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t("This assessment has been deleted.")}
                    </Typography>
                  )}
                </>
              ) : null}
            </Stack>
          ) : null}
        </Box>
      </Box>
    </Drawer>
  );
}
